import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { UserCircle, PanelLeftClose, PanelLeftOpen, LogOut } from 'lucide-react';
import { useAuthStore } from '../store/auth';
import { NAV_ITEMS, PRIMARY_COUNT } from '../app/nav';
import { useConfirm } from './ui/ConfirmProvider';

interface LayoutProps {
  children: React.ReactNode;
}

export const Layout: React.FC<LayoutProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, rbac, seller } = useAuthStore();
  const confirm = useConfirm();
  const shopName = seller.shop?.name || 'Amanah';

  // Sidebar keng ekranda yig'iladi. Tanlov saqlanadi — har safar qaytadan
  // yopish kerak bo'lmasin. Planshetda (md–lg) u baribir faqat ikonka.
  const [sidebarOpen, setSidebarOpen] = useState(
    () => localStorage.getItem('sidebar') !== 'collapsed',
  );

  const toggleSidebar = () => {
    setSidebarOpen((open) => {
      localStorage.setItem('sidebar', open ? 'collapsed' : 'open');
      return !open;
    });
  };

  /** Yorliqlar faqat lg'da va sidebar ochiq bo'lganda ko'rinadi. */
  const labelClass = sidebarOpen ? 'hidden lg:inline' : 'hidden';

  // Bo'lim ruxsat bo'lsagina ko'rinadi. Bu faqat UX — route qo'riqchisi va
  // server ham shu ruxsatni tekshiradi.
  const visibleNavItems = NAV_ITEMS.filter((item) => item.need in rbac);
  const primaryNavItems = visibleNavItems.slice(0, PRIMARY_COUNT);

  const handleLogout = async () => {
    const ok = await confirm({
      title: 'Chiqish',
      description: 'Siz rostdan ham chiqishni hohlaysizmi?',
      confirmLabel: 'Ha, chiqaman',
      cancelLabel: "Yo'q",
      danger: true,
    });
    if (!ok) return;
    logout();
    navigate('/login');
  };

  const isActive = (path: string) => location.pathname === path;
  const isProfile = location.pathname.startsWith('/profile');

  return (
    // Balandlik ekranga teng va scroll faqat `main` ichida — shunda sahifa
    // sarlavhalari (`.page-bar`) scroll qilganda yuqorida qotib turadi.
    // Ilgari butun oyna scroll bo'lardi va `main` dagi sticky ishlamasdi.
    // Tepadagi safe-area (iOS ilovada status bar) shu yerda — `main` ichida
    // bo'lsa sticky sarlavha status bar ostiga yopishib qolardi.
    <div className="h-[100dvh] flex overflow-hidden bg-[#f8faf9] pt-[env(safe-area-inset-top)]">
      {/* Sidebar — planshetda ikonka rail, desktopda yig'iladi. md'dan pastda
          yashiringan: u yerda pastki panel ishlaydi. */}
      <aside
        className={`hidden md:flex md:flex-col md:w-16 shrink-0 overflow-y-auto bg-white border-r border-gray-100
          transition-[width] duration-200 ${sidebarOpen ? 'lg:w-64' : 'lg:w-16'}`}
      >
        <div
          className={`h-16 flex items-center border-b border-gray-100 gap-2
            ${sidebarOpen ? 'justify-center lg:justify-between px-0 lg:px-4' : 'justify-center px-0'}`}
        >
          <Link
            to="/"
            className={`text-xl font-bold text-primary-800 tracking-tight truncate ${
              sidebarOpen ? '' : 'lg:hidden'
            }`}
          >
            <span className="lg:hidden">{shopName.charAt(0).toUpperCase()}</span>
            <span className={labelClass}>{shopName}</span>
          </Link>

          <button
            onClick={toggleSidebar}
            title={sidebarOpen ? 'Menyuni yig\'ish' : 'Menyuni ochish'}
            aria-label={sidebarOpen ? 'Menyuni yig\'ish' : 'Menyuni ochish'}
            className="hidden lg:grid place-items-center w-9 h-9 shrink-0 rounded-lg
              text-gray-400 hover:bg-gray-50 hover:text-gray-700 transition-colors
              focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
          >
            {sidebarOpen ? <PanelLeftClose size={20} /> : <PanelLeftOpen size={20} />}
          </button>
        </div>

        <nav className={`flex-1 space-y-1 p-2 ${sidebarOpen ? 'lg:p-4' : 'lg:p-2'}`}>
          {visibleNavItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);

            return (
              <Link
                key={item.path}
                to={item.path}
                title={item.label}
                className={`flex items-center gap-3 py-3 rounded-lg transition-colors
                  ${sidebarOpen ? 'justify-center lg:justify-start px-0 lg:px-4' : 'justify-center px-0'}
                  ${active ? 'bg-primary-50 text-primary-800' : 'text-gray-500 hover:bg-gray-50 hover:text-gray-800'}`}
              >
                <Icon size={20} className="shrink-0" />
                <span className={`${labelClass} font-medium text-sm`}>{item.label}</span>
              </Link>
            );
          })}
        </nav>

        {/* Profil va Chiqish — sidebar tagida. Mobil yuzada ikkalasi Profil sahifasida. */}
        <div className={`border-t border-gray-100 p-2 space-y-1 ${sidebarOpen ? 'lg:p-4' : 'lg:p-2'}`}>
          <Link
            to="/profile"
            title="Profil"
            className={`flex items-center gap-3 py-3 rounded-lg transition-colors
              ${sidebarOpen ? 'justify-center lg:justify-start px-0 lg:px-4' : 'justify-center px-0'}
              ${isProfile ? 'bg-primary-50 text-primary-800' : 'text-gray-500 hover:bg-gray-50 hover:text-gray-800'}`}
          >
            <UserCircle size={20} className="shrink-0" />
            <span className={`${labelClass} font-medium text-sm`}>Profil</span>
          </Link>
          <button
            onClick={handleLogout}
            title="Chiqish"
            className={`w-full flex items-center gap-3 py-3 rounded-lg
              ${sidebarOpen ? 'justify-center lg:justify-start px-0 lg:px-4' : 'justify-center px-0'}
              text-gray-500 hover:bg-danger-50 hover:text-danger-600 transition-colors
              focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-danger-500`}
          >
            <LogOut size={20} className="shrink-0" />
            <span className={`${labelClass} font-medium text-sm`}>
              Chiqish{user?.username ? ` — ${user.username}` : ''}
            </span>
          </button>
        </div>
      </aside>

      {/* Main content — yuqori panel yo'q: sahifa nomi navigatsiyada ko'rinadi,
          har ekranning o'z qidiruvi va orqaga tugmasi bor. */}
      <div className="flex-1 flex flex-col min-w-0">
        <main className="flex-1 overflow-auto p-4 lg:p-6 pb-24 md:pb-6">
          <div className="content-shell">{children}</div>
        </main>
      </div>

      {/* Mobile bottom navigation */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-30 bg-white border-t border-gray-100 grid grid-cols-5 pb-[env(safe-area-inset-bottom)]">
        {primaryNavItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.path);
          return (
            <Link
              key={item.path}
              to={item.path}
              className={`flex flex-col items-center justify-center gap-1 py-2.5 min-h-[56px] ${
                active ? 'text-primary-800' : 'text-gray-400'
              }`}
            >
              <Icon size={22} />
              <span className="text-[11px] font-medium leading-none">{item.label}</span>
            </Link>
          );
        })}
        {/* Profil doim turadi: qolgan bo'limlar, Obuna va Chiqish shu yerda. */}
        <Link
          to="/profile"
          className={`flex flex-col items-center justify-center gap-1 py-2.5 min-h-[56px] ${
            isProfile ? 'text-primary-800' : 'text-gray-400'
          }`}
        >
          <UserCircle size={22} />
          <span className="text-[11px] font-medium leading-none">Profil</span>
        </Link>
      </nav>

    </div>
  );
};
